import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { getPublicApiBase } from '../utils/publicApi';
import {
  getStoredLayout,
  setStoredLayout,
  getStoredTemplate,
  setStoredTemplate,
  getStoredContentTemplate,
  setStoredContentTemplate,
  getStoredSplitLeftTemplateOverride,
  setStoredSplitLeftTemplateOverride,
  clearStoredSplitLeftTemplateOverride,
  getStoredSplitRightTemplateOverride,
  setStoredSplitRightTemplateOverride,
  clearStoredSplitRightTemplateOverride
} from '../utils/publicPortalPreferences';
import {
  LOGIN_PORTAL_THEMES,
  LOGIN_PORTAL_DEFAULT_TEMPLATE,
  isValidPortalTemplateId
} from '../theme/loginPortalThemes';
import { parseBrandingStyle } from '../utils/brandingStyles';

const PublicPortalAppearanceContext = createContext(null);

const emptyBranding = {
  portalLogoPath: null,
  companyName: null,
  companyAddress: null,
  companyCity: null,
  companyZip: null,
  companyWebsite: null,
  companyContactEmail: null,
  companyTagline: null,
  companyNameStyle: null,
  companyTaglineStyle: null
};

function parseBrandingPayload(data) {
  if (!data || typeof data !== 'object') return { ...emptyBranding };
  const trim = (v) => {
    const s = v != null ? String(v).trim() : '';
    return s || null;
  };
  return {
    portalLogoPath: trim(data.portalLogoPath),
    companyName: trim(data.companyName),
    companyAddress: trim(data.companyAddress),
    companyCity: trim(data.companyCity),
    companyZip: trim(data.companyZip),
    companyWebsite: trim(data.companyWebsite),
    companyContactEmail: trim(data.companyContactEmail),
    companyTagline: trim(data.companyTagline),
    companyNameStyle: parseBrandingStyle(data.companyNameStyle),
    companyTaglineStyle: parseBrandingStyle(data.companyTaglineStyle)
  };
}

export function PublicPortalAppearanceProvider({ children }) {
  const [layoutMode, setLayoutModeState] = useState(() => getStoredLayout());
  const [templateId, setTemplateIdState] = useState(() => getStoredTemplate());
  const [contentTemplateId, setContentTemplateIdState] = useState(() => getStoredContentTemplate());
  const [splitLeftTemplateOverride, setSplitLeftTemplateOverrideState] = useState(() =>
    getStoredSplitLeftTemplateOverride()
  );
  const [splitRightTemplateOverride, setSplitRightTemplateOverrideState] = useState(() =>
    getStoredSplitRightTemplateOverride()
  );
  const [publicBranding, setPublicBranding] = useState(() => ({ ...emptyBranding }));

  const setLayoutMode = useCallback((mode) => {
    const v = mode === 'regular' ? 'regular' : 'split';
    setLayoutModeState(v);
    setStoredLayout(v);
  }, []);

  const setTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setTemplateIdState(v);
    setStoredTemplate(v);
  }, []);

  const setContentTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setContentTemplateIdState(v);
    setStoredContentTemplate(v);
  }, []);

  const splitBrowseTemplateId = splitLeftTemplateOverride ?? contentTemplateId;
  const splitPreviewTemplateId = splitRightTemplateOverride ?? contentTemplateId;

  const setSplitBrowseTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setSplitLeftTemplateOverrideState(v);
    setStoredSplitLeftTemplateOverride(v);
  }, []);

  const setSplitPreviewTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setSplitRightTemplateOverrideState(v);
    setStoredSplitRightTemplateOverride(v);
  }, []);

  const clearSplitBrowseTemplateOverride = useCallback(() => {
    setSplitLeftTemplateOverrideState(null);
    clearStoredSplitLeftTemplateOverride();
  }, []);

  const clearSplitPreviewTemplateOverride = useCallback(() => {
    setSplitRightTemplateOverrideState(null);
    clearStoredSplitRightTemplateOverride();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const base = getPublicApiBase().replace(/\/+$/, '');
        const r = await axios.get(`${base}/branding`);
        const parsed = parseBrandingPayload(r.data?.data);
        if (!cancelled) setPublicBranding(parsed);
      } catch {
        if (!cancelled) setPublicBranding({ ...emptyBranding });
      }
    };
    load();
    const onBrandingUpdated = () => {
      load();
    };
    window.addEventListener('erp4c-branding-updated', onBrandingUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('erp4c-branding-updated', onBrandingUpdated);
    };
  }, []);

  const pageBackgroundCss = useMemo(() => {
    const t = LOGIN_PORTAL_THEMES[templateId] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
    return t.background;
  }, [templateId]);

  /** Theme slice for public portal cards, split list/preview columns, etc. */
  const contentPanelTheme = useMemo(() => {
    return LOGIN_PORTAL_THEMES[contentTemplateId] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
  }, [contentTemplateId]);

  /** Split layout — left browse column (inherits content template unless overridden). */
  const splitBrowsePanelTheme = useMemo(() => {
    return LOGIN_PORTAL_THEMES[splitBrowseTemplateId] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
  }, [splitBrowseTemplateId]);

  /** Split layout — right preview column (inherits content template unless overridden). */
  const splitPreviewPanelTheme = useMemo(() => {
    return LOGIN_PORTAL_THEMES[splitPreviewTemplateId] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
  }, [splitPreviewTemplateId]);

  const refreshBranding = useCallback(async () => {
    try {
      const base = getPublicApiBase().replace(/\/+$/, '');
      const r = await axios.get(`${base}/branding`);
      setPublicBranding(parseBrandingPayload(r.data?.data));
    } catch {
      setPublicBranding({ ...emptyBranding });
    }
  }, []);

  const value = useMemo(
    () => ({
      layoutMode,
      setLayoutMode,
      templateId,
      setTemplateId,
      contentTemplateId,
      setContentTemplateId,
      contentPanelTheme,
      splitBrowseTemplateId,
      splitPreviewTemplateId,
      splitBrowsePanelTheme,
      splitPreviewPanelTheme,
      setSplitBrowseTemplateId,
      setSplitPreviewTemplateId,
      clearSplitBrowseTemplateOverride,
      clearSplitPreviewTemplateOverride,
      splitBrowseHasOverride: splitLeftTemplateOverride != null,
      splitPreviewHasOverride: splitRightTemplateOverride != null,
      pageBackgroundCss,
      publicBranding,
      /** @deprecated use publicBranding.portalLogoPath */
      portalLogoPath: publicBranding.portalLogoPath,
      refreshBranding
    }),
    [
      layoutMode,
      setLayoutMode,
      templateId,
      setTemplateId,
      contentTemplateId,
      setContentTemplateId,
      contentPanelTheme,
      splitBrowseTemplateId,
      splitPreviewTemplateId,
      splitBrowsePanelTheme,
      splitPreviewPanelTheme,
      setSplitBrowseTemplateId,
      setSplitPreviewTemplateId,
      clearSplitBrowseTemplateOverride,
      clearSplitPreviewTemplateOverride,
      splitLeftTemplateOverride,
      splitRightTemplateOverride,
      pageBackgroundCss,
      publicBranding,
      refreshBranding
    ]
  );

  return (
    <PublicPortalAppearanceContext.Provider value={value}>{children}</PublicPortalAppearanceContext.Provider>
  );
}

export function usePublicPortalAppearance() {
  const ctx = useContext(PublicPortalAppearanceContext);
  if (!ctx) {
    throw new Error('usePublicPortalAppearance must be used within PublicPortalAppearanceProvider');
  }
  return ctx;
}

export function usePublicPortalAppearanceOptional() {
  return useContext(PublicPortalAppearanceContext);
}
